import React, { useState, useEffect } from 'react';
import { ArrowLeft, Building, Truck, FileSpreadsheet, Edit2, Ban, CheckCircle, Loader2, X, Phone, Mail, MapPin, User, Route } from 'lucide-react';
import { getClientById, toggleClientStatus } from '../../api/createClientApi';
import { getAllVehicleClientAssignments } from '../../api/vehicalsManagementApi';
import { getAllDrivers } from '../../api/driverApi';
import { getAllSlabs } from '../../api/createClientApi';
import { getAllRoutes } from '../../api/routesApi';
import { getTripSheetsByClientId } from '../../api/tripSheetApi';
import Breadcrumb from '../shared/Breadcrumb';
import EditClientModal from './EditClientModal';
import { toast } from '../shared/Toast';
import { showConfirm } from '../shared/ConfirmContext';

const InfoCard = ({ label, value, accent }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', textAlign: 'center' }}>
    <div style={{ fontSize: '28px', fontWeight: 700, color: accent || '#07A95B' }}>{value}</div>
    <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>{label}</div>
  </div>
);

const TabButton = ({ id, label, icon: Icon, active, onClick }) => (
  <button onClick={() => onClick(id)} style={{
    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: active ? 700 : 500, color: active ? '#20B967' : '#888',
    borderBottom: active ? '2px solid #20B967' : '2px solid transparent', marginBottom: '-2px', transition: 'all 0.2s',
  }}>
    <Icon size={16} /> {label}
  </button>
);

const VehiclesTab = ({ clientId }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    if (clientId) {
      fetchVehicles();
      fetchDrivers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Update driver names when drivers data changes
  useEffect(() => {
    if (vehicles.length > 0 && drivers.length > 0) {
      setVehicles(prevVehicles => prevVehicles.map(v => {
        const driver = drivers.find(d => d.vehicleId === v.id || d.vehicle_id === v.id);
        return {
          ...v,
          driverName: driver?.driverName || driver?.name || v.driverName || 'Unassigned'
        };
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers]);

  const fetchDrivers = async () => {
    try {
      const data = await getAllDrivers();
      const driversArray = data.drivers || data.data || data || [];
      setDrivers(driversArray);
    } catch (err) {
    }
  };

  // Fetch all vehicle assignments across all pages
  const fetchAllVehicleAssignments = async () => {
    try {
      const firstResponse = await getAllVehicleClientAssignments(1, 100);
      
      const totalCount = firstResponse.totalCount || firstResponse.pagination?.total || 0;
      const assignments = firstResponse.assignments || firstResponse.data || [];
      
      
      // Calculate pages based on actual items per page, not requested limit
      // API might return 10 per page even if we request 100
      const itemsPerPage = assignments.length || 10;
      const totalPages = Math.ceil(totalCount / itemsPerPage);
      
      
      if (totalPages <= 1) return assignments;
      
      const allAssignments = [...assignments];
      
      for (let page = 2; page <= totalPages; page++) {
        const response = await getAllVehicleClientAssignments(page, 100);
        const pageAssignments = response.assignments || response.data || [];
        allAssignments.push(...pageAssignments);
      }
      
      return allAssignments;
    } catch (err) {
      return [];
    }
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const allAssignments = await fetchAllVehicleAssignments();
      
      
      // Filter assignments by clientId and extract vehicle details
      const clientAssignments = allAssignments.filter(
        a => a.clientId === clientId || a.client?.id === clientId
      );
      
      
      // Map assignments to vehicle objects - driver is now included from API
      const vehiclesData = clientAssignments.map(a => ({
        id: a.vehicle?.id || a.vehicleId,
        vehicleNumber: a.vehicle?.vehicleNumber || a.vehicle?.vehicle_number || a.vehicle?.number,
        registrationNumber: a.vehicle?.registrationNumber || a.vehicle?.regNo || a.registrationNumber,
        vehicleType: a.vehicle?.vehicleType || a.vehicle?.type || a.vehicleType,
        driverName: a.vehicle?.driver?.driverName || a.vehicle?.driver?.name || 'Unassigned',
        status: a.vehicle?.status || a.status || 'Active'
      })).filter(v => v.id); // Remove any without vehicle ID

      setVehicles(vehiclesData);
    } catch (err) {
      setError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', color: '#666' }}>
        <Loader2 size={24} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} /> Loading vehicles...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#F04438', backgroundColor: '#FEF2F2', borderRadius: '8px' }}>
        {error}
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: '#888', backgroundColor: 'white', borderRadius: '8px' }}>
        No vehicles assigned to this client.
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', backgroundColor: '#212A30', color: 'white', padding: '12px 16px', fontSize: '13px', fontWeight: 500 }}>
        <div style={{ flex: 1.5 }}>Vehicle Number</div>
        <div style={{ flex: 1, textAlign: 'center' }}>Type</div>
        <div style={{ flex: 1.5, textAlign: 'center' }}>Driver</div>
        <div style={{ flex: 0.8, textAlign: 'center' }}>Status</div>
      </div>
      <div style={{ maxHeight: '246px', overflowY: 'auto' }}>
        {vehicles.map((v, i) => (
          <div key={i} style={{ display: 'flex', padding: '12px 16px', borderBottom: '1px solid #F0F0F0', fontSize: '14px', color: '#333', alignItems: 'center', backgroundColor: 'white' }}>
            <div style={{ flex: 1.5, fontFamily: 'monospace', fontWeight: 500 }}>{v.vehicleNumber || v.registrationNumber || v.regNo || 'N/A'}</div>
            <div style={{ flex: 1, textAlign: 'center', color: '#555' }}>{v.vehicleType || v.type || 'N/A'}</div>
            <div style={{ flex: 1.5, textAlign: 'center', color: '#555' }}>{v.driverName || v.driver || 'Unassigned'}</div>
            <div style={{ flex: 0.8, textAlign: 'center' }}>
              <span style={{ padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, backgroundColor: v.status === 'Active' ? '#EEF8F3' : '#F5F5F5', color: v.status === 'Active' ? '#20B967' : '#888' }}>{v.status || 'Active'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TripSheetsTab = ({ clientId }) => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (clientId) {
      fetchTripSheets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchTripSheets = async () => {
    try {
      setLoading(true);
      const data = await getTripSheetsByClientId(clientId);
      
      // Handle different response formats
      const tripSheetsArray = data?.tripSheets || data?.data || data?.sheets || [];
      
      // Map API data to display format
      const mappedSheets = tripSheetsArray.map(sheet => ({
        period: sheet.period || sheet.month || formatPeriod(sheet.createdAt),
        trips: sheet.totalTrips || sheet.trips || sheet.tripCount || 0,
        amount: sheet.amount || sheet.totalAmount || sheet.billingAmount || '₹0',
        status: sheet.status || sheet.paymentStatus || 'Pending'
      }));
      
      setSheets(mappedSheets);
    } catch (err) {
      setError('Failed to load trip sheets');
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', color: '#666' }}>
        <Loader2 size={24} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} /> Loading trip sheets...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#F04438', backgroundColor: '#FEF2F2', borderRadius: '8px' }}>
        {error}
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: '#888', backgroundColor: 'white', borderRadius: '8px' }}>
        No trip sheets found for this client.
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', backgroundColor: '#212A30', color: 'white', padding: '12px 16px', fontSize: '13px', fontWeight: 500 }}>
        <div style={{ flex: 1 }}>Period</div>
        <div style={{ flex: 1, textAlign: 'center' }}>Total Trips</div>
        <div style={{ flex: 1, textAlign: 'center' }}>Amount</div>
        <div style={{ flex: 1, textAlign: 'center' }}>Status</div>
      </div>
      <div style={{ maxHeight: '246px', overflowY: 'auto' }}>
        {sheets.map((s, i) => (
          <div key={i} style={{ display: 'flex', padding: '12px 16px', borderBottom: '1px solid #F0F0F0', fontSize: '14px', color: '#333', alignItems: 'center', backgroundColor: 'white' }}>
            <div style={{ flex: 1, fontWeight: 500 }}>{s.period}</div>
            <div style={{ flex: 1, textAlign: 'center', color: '#555' }}>{s.trips}</div>
            <div style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>{s.amount}</div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <span style={{ padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, backgroundColor: s.status === 'Paid' ? '#EEF8F3' : '#FFF7E6', color: s.status === 'Paid' ? '#20B967' : '#F57C00' }}>{s.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SlabsPopup = ({ isOpen, onClose, clientId, clientName }) => {
  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && clientId) {
      fetchSlabs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientId]);

  const fetchSlabs = async () => {
    try {
      setLoading(true);
      const data = await getAllSlabs({ clientId });
      const slabsArray = data?.clientStores || data?.slabs || data?.data || [];
      
      const mappedSlabs = slabsArray.map((s) => ({
        id: s.id,
        slabType: s.slabType || s.slab_type || 'KM Based',
        from: s.startRange || s.start_range || s.fromKm || s.from || '0',
        to: s.endRange || s.end_range || s.toKm || s.to || '-',
        sedanRate: s.sedanRate || s.sedan_rate || '-',
        suvRate: s.suvRate || s.suv_rate || '-',
        escortSedanRate: s.escortForSedanRate || s.escort_for_sedan_rate || s.escortSedanRate || s.escort_sedan_rate || s.escortRate || s.escort_rate || '-',
        escortSuvRate: s.escortForSuvRate || s.escort_for_suv_rate || s.escortSuvRate || s.escort_suv_rate || s.escortRate || s.escort_rate || '-'
      }));
      
      setSlabs(mappedSlabs);
    } catch (err) {
      setError('Failed to load slabs');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === '-' || !value) return '-';
    return `₹${value}`;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '85vh',
        overflow: 'hidden',
        boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
        animation: 'popupSlideIn 0.3s ease-out'
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #07A95B 0%, #059669 100%)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileSpreadsheet size={20} color="white" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'white' }}>
                Rate Slabs
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                {clientName} • {slabs.length} slab{slabs.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            cursor: 'pointer',
            padding: '10px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <X size={22} color="white" />
          </button>
        </div>

        {/* Content */}
        <div style={{ maxHeight: 'calc(85vh - 84px)', overflow: 'auto', backgroundColor: '#F8FAFC' }}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: '60px',
              gap: '16px',
              color: '#64748B' 
            }}>
              <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#07A95B' }} />
              <span style={{ fontSize: '15px' }}>Loading rate slabs...</span>
            </div>
          ) : error ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Ban size={28} color="#EF4444" />
              </div>
              <p style={{ color: '#EF4444', fontSize: '16px', margin: '0 0 8px', fontWeight: 500 }}>{error}</p>
              <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>Please try again later</p>
            </div>
          ) : slabs.length === 0 ? (
            <div style={{ padding: '50px', textAlign: 'center' }}>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: '#F1F5F9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <FileSpreadsheet size={32} color="#94A3B8" />
              </div>
              <p style={{ color: '#334155', fontSize: '17px', margin: '0 0 8px', fontWeight: 600 }}>No Slabs Configured</p>
              <p style={{ color: '#64748B', fontSize: '14px', margin: 0 }}>This client doesn't have any rate slabs set up yet.</p>
            </div>
          ) : (
            <div style={{ padding: '20px' }}>
              {/* Header Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr 1fr 1fr 1fr 1fr',
                gap: '16px',
                padding: '14px 20px',
                backgroundColor: '#1E293B',
                borderRadius: '10px',
                marginBottom: '12px'
              }}>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Range (KM)</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Sedan</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>SUV</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Sedan Escort</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>SUV Escort</div>
              </div>

              {/* Slab Cards */}
              {slabs.map((s, i) => (
                <div key={i} style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr 1fr 1fr 1fr 1fr',
                  gap: '16px',
                  padding: '18px 20px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  marginBottom: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid #E2E8F0',
                  transition: 'all 0.2s'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  {/* Type Badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: '#EEF8F3',
                      color: '#07A95B'
                    }}>
                      {s.slabType}
                    </span>
                  </div>

                  {/* Range */}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#334155' }}>
                      {s.from} - {s.to}
                    </span>
                    <span style={{ display: 'block', fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>kilometers</span>
                  </div>

                  {/* Sedan Rate */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#059669' }}>
                      {formatCurrency(s.sedanRate)}
                    </span>
                  </div>

                  {/* SUV Rate */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#0891B2' }}>
                      {formatCurrency(s.suvRate)}
                    </span>
                  </div>

                  {/* Escort Sedan Rate */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#7C3AED' }}>
                      {formatCurrency(s.escortSedanRate)}
                    </span>
                  </div>

                  {/* Escort SUV Rate */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#9333EA' }}>
                      {formatCurrency(s.escortSuvRate)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes popupSlideIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const RoutesPopup = ({ isOpen, onClose, clientId, clientName }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && clientId) {
      fetchRoutes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientId]);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const data = await getAllRoutes();
      const routesArray = data?.routes || data?.data || [];
      
      const clientRoutes = routesArray.filter(r =>
        r.clientId === clientId || r.client_id === clientId
      );
      
      const mappedRoutes = clientRoutes.map((r) => ({
        id: r.id,
        routeName: r.routeName || r.route_name || 'Unnamed Route',
        fromLocation: r.fromLocation || r.from_location || '-',
        toLocation: r.toLocation || r.to_location || '-',
        distance: r.distanceKm || r.distance_km || r.distance || '0'
      }));
      
      setRoutes(mappedRoutes);
    } catch (err) {
      setError('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '85vh',
        overflow: 'hidden',
        boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
        animation: 'popupSlideIn 0.3s ease-out'
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Route size={20} color="white" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'white' }}>
                Routes
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                {clientName} • {routes.length} route{routes.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            cursor: 'pointer',
            padding: '10px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <X size={22} color="white" />
          </button>
        </div>

        {/* Content */}
        <div style={{ maxHeight: 'calc(85vh - 84px)', overflow: 'auto', backgroundColor: '#F8FAFC' }}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: '60px',
              gap: '16px',
              color: '#64748B' 
            }}>
              <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#0284C7' }} />
              <span style={{ fontSize: '15px' }}>Loading routes...</span>
            </div>
          ) : error ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Ban size={28} color="#EF4444" />
              </div>
              <p style={{ color: '#EF4444', fontSize: '16px', margin: '0 0 8px', fontWeight: 500 }}>{error}</p>
              <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>Please try again later</p>
            </div>
          ) : routes.length === 0 ? (
            <div style={{ padding: '50px', textAlign: 'center' }}>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: '#F1F5F9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Route size={32} color="#94A3B8" />
              </div>
              <p style={{ color: '#334155', fontSize: '17px', margin: '0 0 8px', fontWeight: 600 }}>No Routes Configured</p>
              <p style={{ color: '#64748B', fontSize: '14px', margin: 0 }}>This client doesn't have any routes set up yet.</p>
            </div>
          ) : (
            <div style={{ padding: '20px' }}>
              {/* Header Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr 1fr 100px',
                gap: '16px',
                padding: '14px 20px',
                backgroundColor: '#1E293B',
                borderRadius: '10px',
                marginBottom: '12px'
              }}>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Route Name</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>From</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>To</div>
                <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Distance</div>
              </div>

              {/* Route Cards */}
              {routes.map((r, i) => (
                <div key={i} style={{
                  display: 'grid',
                  gridTemplateColumns: '1.5fr 1fr 1fr 100px',
                  gap: '16px',
                  padding: '18px 20px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  marginBottom: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid #E2E8F0',
                  transition: 'all 0.2s'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  {/* Route Name */}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: '#E0F2FE',
                      color: '#0284C7'
                    }}>
                      {r.routeName}
                    </span>
                  </div>

                  {/* From Location */}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#334155' }}>
                      {r.fromLocation}
                    </span>
                  </div>

                  {/* To Location */}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#334155' }}>
                      {r.toLocation}
                    </span>
                  </div>

                  {/* Distance */}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#059669' }}>
                      {r.distance} km
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaymentHistoryTab = () => {
  const payments = [
    { date: '05 Feb, 2026', amount: '₹2,84,000', txnId: 'AXS2026011234', status: 'Success' },
    { date: '10 Jan, 2026', amount: '₹3,10,000', txnId: 'AXS2025121005', status: 'Success' },
    { date: '08 Dec, 2025', amount: '₹2,60,000', txnId: 'AXS2025111889', status: 'Success' },
  ];
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', backgroundColor: '#212A30', color: 'white', padding: '12px 16px', fontSize: '13px', fontWeight: 500 }}>
        <div style={{ flex: 1 }}>Date</div>
        <div style={{ flex: 1, textAlign: 'center' }}>Amount</div>
        <div style={{ flex: 2, textAlign: 'center' }}>Axis Txn ID</div>
        <div style={{ flex: 0.8, textAlign: 'center' }}>Status</div>
      </div>
      {payments.map((p, i) => (
        <div key={i} style={{ display: 'flex', padding: '12px 16px', borderBottom: '1px solid #F0F0F0', fontSize: '14px', color: '#333', alignItems: 'center', backgroundColor: 'white' }}>
          <div style={{ flex: 1, color: '#555' }}>{p.date}</div>
          <div style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>{p.amount}</div>
          <div style={{ flex: 2, textAlign: 'center', fontFamily: 'monospace', fontSize: '12px', color: '#888' }}>{p.txnId}</div>
          <div style={{ flex: 0.8, textAlign: 'center' }}>
            <span style={{ padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, backgroundColor: '#EEF8F3', color: '#20B967' }}>{p.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const ClientDetail = ({ client, onBack }) => {
  const [activeTab, setActiveTab] = useState('vehicles');
  const [clientDetails, setClientDetails] = useState(client || { companyName: '', contact: '', email: '', vehicles: 0, slabs: 0, rateType: 'KM Based', billing: 'Monthly' });
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSlabsPopupOpen, setIsSlabsPopupOpen] = useState(false);
  const [isRoutesPopupOpen, setIsRoutesPopupOpen] = useState(false);
  const [routesCount, setRoutesCount] = useState(0);
  const [slabsCount, setSlabsCount] = useState(0);
  
  // Trip sheet summary stats
  const [totalTrips, setTotalTrips] = useState(0);
  const [amountDue, setAmountDue] = useState('₹0');
  const [totalPaid, setTotalPaid] = useState('₹0');

  // Fetch client details, slabs count, and routes count if ID available
  useEffect(() => {
    if (client?.id) {
      fetchClientDetails(client.id);
      fetchSlabsCount(client.id);
      fetchRoutesCount(client.id);
      fetchTripSheetSummary(client.id);
    }
  }, [client?.id]);

  const fetchClientDetails = async (id) => {
    try {
      setLoading(true);
      const data = await getClientById(id);
      if (data.success && data.client) {
        setClientDetails(data.client);
      }
    } catch (err) {
      console.error('Failed to load client details', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlabsCount = async (clientId) => {
    try {
      const data = await getAllSlabs({ clientId });
      const slabsArray = data?.clientStores || data?.slabs || data?.data || [];
      setSlabsCount(slabsArray.length);
    } catch (err) {
    }
  };

  const fetchRoutesCount = async (clientId) => {
    try {
      const data = await getAllRoutes();
      const routesArray = data.routes || data.data || [];
      const clientRoutes = routesArray.filter(r =>
        r.clientId === clientId || r.client_id === clientId
      );
      setRoutesCount(clientRoutes.length);
    } catch (err) {
    }
  };

  const fetchTripSheetSummary = async (clientId) => {
    try {
      const data = await getTripSheetsByClientId(clientId);
      
      // Handle different response formats
      const tripSheetsArray = data?.tripSheets || data?.data || data?.sheets || [];
      
      let allTrips = 0;
      let dueAmount = 0;
      let paidAmount = 0;
      
      tripSheetsArray.forEach(sheet => {
        // Get trips count
        const trips = sheet.totalTrips || sheet.trips || sheet.tripCount || 0;
        
        // Get amount (parse numeric value)
        const amountStr = sheet.amount || sheet.totalAmount || sheet.billingAmount || '0';
        const amount = parseFloat(amountStr.toString().replace(/[₹,]/g, '')) || 0;
        
        // Get status
        const status = (sheet.status || sheet.paymentStatus || 'Pending').toLowerCase();
        
        // Calculate total trips (all time)
        allTrips += parseInt(trips) || 0;
        
        if (status === 'paid' || status === 'completed') {
          paidAmount += amount;
        } else {
          dueAmount += amount;
        }
      });
      
      // Format amounts
      const formatAmount = (val) => {
        if (val >= 100000) {
          return `₹${(val / 100000).toFixed(1)}L`;
        }
        return `₹${val.toLocaleString('en-IN')}`;
      };
      
      setTotalTrips(allTrips);
      setAmountDue(formatAmount(dueAmount));
      setTotalPaid(formatAmount(paidAmount));
    } catch (err) {
    }
  };

  const c = clientDetails;

  const handleToggleStatus = async () => {
    const isDeactivating = c.isActive !== false;
    const action = isDeactivating ? 'deactivate' : 'activate';
    const confirmed = await showConfirm({
      title: isDeactivating ? 'Deactivate Client' : 'Activate Client',
      message: `Are you sure you want to ${action} this client?`,
      danger: isDeactivating,
      type: isDeactivating ? 'danger' : 'success',
      confirmLabel: isDeactivating ? 'Deactivate' : 'Activate',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      await toggleClientStatus(c.id);
      await fetchClientDetails(c.id);
      toast.success(`Client ${isDeactivating ? 'deactivated' : 'activated'} successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${action} client`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Top Bar */}
      <div style={{ backgroundColor: '#EEF8F3', borderRadius: '8px', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#555', padding: 0 }}><ArrowLeft size={20} /></button>
          <Breadcrumb items={[{ label: 'Clients', onClick: onBack }, { label: c.clientName || c.companyName }]} />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => setIsEditModalOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px solid #D0D5DD', borderRadius: '6px', backgroundColor: 'white', color: '#555', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
            <Edit2 size={14} /> Edit
          </button>
          <button 
            onClick={handleToggleStatus}
            disabled={loading}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              padding: '8px 16px', 
              border: 'none', 
              borderRadius: '6px', 
              backgroundColor: c.isActive !== false ? '#FCE8E8' : '#EEF8F3', 
              color: c.isActive !== false ? '#F04438' : '#20B967', 
              fontSize: '13px', 
              fontWeight: 500, 
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {c.isActive !== false ? <Ban size={14} /> : <CheckCircle size={14} />} 
            {c.isActive !== false ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Profile Card */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '24px' }}>
        <div style={{ width: '72px', height: '72px', borderRadius: '16px', backgroundColor: '#EEF8F3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Building size={36} color="#20B967" />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 700, color: '#333' }}>{c.clientName || c.companyName}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
              <User size={16} color="#20B967" />
              <span>{c.contactPerson || c.contact || 'N/A'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
              <Phone size={16} color="#20B967" />
              <span>{c.contactNumber || c.phone || 'N/A'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
              <Mail size={16} color="#20B967" />
              <span>{c.email || 'N/A'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
              <MapPin size={16} color="#20B967" />
              <span>{c.address ? `${c.address}${c.location ? `, ${c.location}` : ''}` : 'N/A'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { label: c.billing || 'Monthly Billing', color: '#F0F4FF', textColor: '#5B6EF4' },
              { label: `${slabsCount} Slabs`, color: '#FFF7E6', textColor: '#F57C00', clickable: true },
              { label: `${routesCount} Routes`, color: '#E0F2FE', textColor: '#0284C7', clickable: true, onClick: () => setIsRoutesPopupOpen(true) },
            ].map((t, i) => (
              <span
                key={i}
                onClick={() => {
                if (t.clickable && t.onClick) t.onClick();
                else if (t.clickable) setIsSlabsPopupOpen(true);
              }}
                style={{
                  padding: '4px 14px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: t.color,
                  color: t.textColor,
                  cursor: t.clickable ? 'pointer' : 'default'
                }}
              >
                {t.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <InfoCard label="Assigned Vehicles" value={c.vehicles ?? 0} />
        <InfoCard label="Total Trips" value={totalTrips} />
        <InfoCard label="Amount Due" value={amountDue} accent="#F57C00" />
        <InfoCard label="Total Paid (All Time)" value={totalPaid} />
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '2px solid #E8E8E8', display: 'flex' }}>
        <TabButton id="vehicles" label="Assigned Vehicles" icon={Truck} active={activeTab === 'vehicles'} onClick={setActiveTab} />
        <TabButton id="sheets" label="Trip Sheets" icon={FileSpreadsheet} active={activeTab === 'sheets'} onClick={setActiveTab} />
        {/* <TabButton id="payments" label="Payment History" icon={CreditCard} active={activeTab === 'payments'} onClick={setActiveTab} /> */}
      </div>

      {activeTab === 'vehicles' && <VehiclesTab clientId={client?.id || client?._id || client?.clientId} />}
      {activeTab === 'sheets' && <TripSheetsTab clientId={client?.id || client?._id || client?.clientId} />}
      {activeTab === 'payments' && <PaymentHistoryTab />}

      <SlabsPopup 
        isOpen={isSlabsPopupOpen} 
        onClose={() => setIsSlabsPopupOpen(false)} 
        clientId={client?.id}
        clientName={c.companyName}
      />

      <RoutesPopup 
        isOpen={isRoutesPopupOpen} 
        onClose={() => setIsRoutesPopupOpen(false)} 
        clientId={client?.id || client?._id || client?.clientId}
        clientName={c.companyName}
      />
      
      <EditClientModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        clientId={client?.id}
        onSave={() => {
          fetchClientDetails(client?.id);
          fetchSlabsCount(client?.id);
          fetchRoutesCount(client?.id);
          setIsSlabsPopupOpen(false);
        }}
      />
    </div>
  );
};

export default ClientDetail;
